/**
 * Service de configuration de carte multi-provider.
 * Récupère la configuration du backend et gère le basculement
 * entre Leaflet (OSM) et Mapbox selon le provider actif.
 */

import api from '@/lib/api';

// Configuration par défaut (OSM)
const DEFAULT_CONFIG = {
  provider: 'openstreetmap',
  tile_url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  fallback_enabled: true
};

// Cache de la configuration
let cachedConfig = null;
let configPromise = null;

/**
 * Récupère la configuration de carte depuis le backend.
 * Utilise un cache pour éviter les appels répétés.
 */
export async function getMapConfig() {
  // Retourner le cache si disponible
  if (cachedConfig) {
    return cachedConfig;
  }
  
  // Éviter les appels concurrents
  if (configPromise) {
    return configPromise;
  }
  
  configPromise = fetchMapConfig();
  return configPromise;
}

async function fetchMapConfig() {
  try {
    const response = await api.get('/geo/config');
    cachedConfig = response.data;
    console.log('🗺️ Configuration carte chargée:', cachedConfig.provider);
    return cachedConfig;
  } catch (error) {
    console.warn('⚠️ Erreur chargement config carte, utilisation OSM par défaut:', error);
    cachedConfig = DEFAULT_CONFIG;
    return cachedConfig;
  } finally {
    configPromise = null;
  }
}

/**
 * Invalide le cache de configuration.
 * Utile si l'utilisateur change le provider dans les paramètres.
 */
export function invalidateMapConfig() {
  cachedConfig = null;
  configPromise = null;
}

/**
 * Vérifie si le provider actif est Mapbox.
 */
export function isMapboxProvider(config) {
  return config?.provider === 'mapbox';
}

/**
 * Vérifie si le provider actif est OpenStreetMap.
 */
export function isOSMProvider(config) {
  return config?.provider === 'openstreetmap' || config?.provider === 'osm';
}

/**
 * Retourne la configuration Leaflet pour le provider.
 */
export function getLeafletConfig(config) {
  if (!config) return DEFAULT_CONFIG;
  
  return {
    tileUrl: config.tile_url || DEFAULT_CONFIG.tile_url,
    attribution: config.attribution || DEFAULT_CONFIG.attribution,
    maxZoom: 19,
    subdomains: ['a', 'b', 'c']
  };
}

/**
 * Recherche de lieux via l'API unifiée.
 */
export async function searchLocations(query, options = {}) {
  const { limit = 5, country = 'CI', lat, lng } = options;
  
  try {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      country
    });
    
    if (lat && lng) {
      params.append('lat', lat.toString());
      params.append('lng', lng.toString());
    }
    
    const response = await api.get(`/geo/search?${params}`);
    return response.data;
  } catch (error) {
    console.error('Erreur recherche lieux:', error);
    return { success: false, suggestions: [], error: error.message };
  }
}

/**
 * Calcule un itinéraire via l'API unifiée.
 */
export async function calculateRoute(origin, destination, profile = 'driving') {
  try {
    const response = await api.post('/geo/route', {
      origin,
      destination,
      profile
    });
    return response.data;
  } catch (error) {
    console.error('Erreur calcul route:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Calcule un itinéraire multi-étapes.
 */
export async function calculateMultiRoute(waypoints, profile = 'driving') {
  try {
    const response = await api.post('/geo/multi-route', {
      waypoints,
      profile
    });
    return response.data;
  } catch (error) {
    console.error('Erreur calcul multi-route:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Teste les providers de géolocalisation.
 */
export async function testProviders() {
  try {
    const response = await api.get('/geo/test-providers');
    return response.data;
  } catch (error) {
    console.error('Erreur test providers:', error);
    return { success: false, error: error.message };
  }
}

export default {
  getMapConfig,
  invalidateMapConfig,
  isMapboxProvider,
  isOSMProvider,
  getLeafletConfig,
  searchLocations,
  calculateRoute,
  calculateMultiRoute,
  testProviders
};
